import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { cartoesApi, transacoesApi } from '../../api/endpoints'
import { Badge } from '../../components/ui/Badge'
import { Button, IconButton } from '../../components/ui/Button'
import { Card, CardBody, CardHeader } from '../../components/ui/Card'
import { EmptyState, LoadingState } from '../../components/ui/EmptyState'
import { Input } from '../../components/ui/Input'
import { Modal } from '../../components/ui/Modal'
import { MonthNavigator } from '../../components/ui/Navigation'
import { Table, THead, Th, ThOrdenavel, Tr, Td } from '../../components/ui/Table'
import { useToast } from '../../components/ui/Toast'
import { ModalidadeCartao, TransactionStatus, TransactionType } from '../../lib/enums'
import { cn, formatDate, formatMoney } from '../../lib/utils'
import { useOrdenarBuscarLocal } from '../../lib/ordenarLancamentos'
import type { CreditCard, RecargaAutomatica } from '../../types'

interface Props {
  cartao: CreditCard
  mes: number
  ano: number
  onMudarMes: (mes: number, ano: number) => void
  onLancarCompra: () => void
  onEditar: () => void
}

const hojeISO = () => new Date().toISOString().slice(0, 10)

/**
 * Painel dos cartões de débito e pré-pago: sem fatura. Mostra as compras do mês (que saem direto da conta) e, no
 * pré-pago, o saldo, a recarga avulsa e a recarga automática.
 */
export function PainelContaCartao({ cartao, mes, ano, onMudarMes, onLancarCompra, onEditar }: Props) {
  const qc = useQueryClient()
  const toast = useToast()
  const prePago = cartao.modalidade === ModalidadeCartao.PrePago

  const { data: lancamentos, isLoading } = useQuery({
    queryKey: ['transacoes', 'cartao', cartao.id, mes, ano],
    queryFn: () => transacoesApi.list({
      cartaoPagamentoId: cartao.id,
      dataInicio: new Date(Date.UTC(ano, mes - 1, 1)).toISOString(),
      dataFim: new Date(Date.UTC(ano, mes, 0, 23, 59, 59)).toISOString(),
      itensPorPagina: 100,
    }),
  })
  const itens = lancamentos?.itens ?? []
  const ordenacao = useOrdenarBuscarLocal(itens, { nome: (t) => t.description, data: (t) => t.date, valor: (t) => t.amount })
  const gasto = itens.filter((t) => t.type === TransactionType.Expense && t.status === TransactionStatus.Confirmed).reduce((s, t) => s + t.amount, 0)
  const recargas = itens.filter((t) => t.type === TransactionType.Income && t.status === TransactionStatus.Confirmed).reduce((s, t) => s + t.amount, 0)

  // ── Recarga avulsa ─────────────────────────────────────────────────────────
  const [recargaAberta, setRecargaAberta] = useState(false)
  const [recarga, setRecarga] = useState({ valor: '', date: hojeISO(), descricao: '' })
  const [erro, setErro] = useState<string | null>(null)

  const atualizarTudo = () => {
    qc.invalidateQueries({ queryKey: ['cartoes'] })
    qc.invalidateQueries({ queryKey: ['transacoes'] })
    qc.invalidateQueries({ queryKey: ['contas'] })
    qc.invalidateQueries({ queryKey: ['dashboard'] })
  }

  const recarregar = useMutation({
    mutationFn: () => cartoesApi.recarregar(cartao.id, {
      valor: Number(recarga.valor),
      date: recarga.date ? new Date(`${recarga.date}T12:00:00Z`).toISOString() : undefined,
      descricao: recarga.descricao.trim() || undefined,
    }),
    onSuccess: (t) => {
      atualizarTudo()
      setRecargaAberta(false)
      toast.success(t.status === TransactionStatus.Pending ? 'Recarga agendada: o saldo sobe quando você der baixa.' : 'Recarga registrada.')
    },
    onError: (e: Error) => setErro(e.message),
  })

  function abrirRecarga() {
    setRecarga({ valor: '', date: hojeISO(), descricao: '' })
    setErro(null)
    setRecargaAberta(true)
  }

  // ── Recarga automática ─────────────────────────────────────────────────────
  const { data: automaticas = [] } = useQuery({
    queryKey: ['cartoes', 'recargas', cartao.id],
    queryFn: () => cartoesApi.listarRecargasAutomaticas(cartao.id),
    enabled: prePago,
  })
  const [formAuto, setFormAuto] = useState<{ aberto: boolean; editando: RecargaAutomatica | null; valor: string; dia: string }>({ aberto: false, editando: null, valor: '', dia: '5' })
  const [cancelando, setCancelando] = useState<RecargaAutomatica | null>(null)

  function abrirAuto(r: RecargaAutomatica | null) {
    setFormAuto({ aberto: true, editando: r, valor: r ? String(r.valor) : '', dia: r ? String(r.dia) : '5' })
    setErro(null)
  }

  const salvarAuto = useMutation({
    mutationFn: () => {
      const valor = Number(formAuto.valor)
      const dia = Number(formAuto.dia)
      const r = formAuto.editando
      if (!r) return cartoesApi.criarRecargaAutomatica(cartao.id, { valor, dia })
      return cartoesApi.atualizarRecargaAutomatica(cartao.id, r.id, {
        valor: valor !== r.valor ? valor : undefined,
        dia: dia !== r.dia ? dia : undefined,
      })
    },
    onSuccess: () => {
      atualizarTudo()
      setFormAuto((f) => ({ ...f, aberto: false }))
      toast.success(formAuto.editando ? 'Recarga automática atualizada.' : 'Recarga automática criada.')
    },
    onError: (e: Error) => setErro(e.message),
  })

  const alternarAuto = useMutation({
    mutationFn: (r: RecargaAutomatica) => cartoesApi.atualizarRecargaAutomatica(cartao.id, r.id, { ativa: !r.ativa }),
    onSuccess: (r) => { atualizarTudo(); toast.success(r.ativa ? 'Recarga automática retomada.' : 'Recarga automática pausada.') },
    onError: (e: Error) => toast.error(e.message),
  })

  const cancelarAuto = useMutation({
    mutationFn: (r: RecargaAutomatica) => cartoesApi.cancelarRecargaAutomatica(cartao.id, r.id),
    onSuccess: () => { atualizarTudo(); setCancelando(null); toast.success('Recarga automática cancelada.') },
    onError: (e: Error) => toast.error(e.message),
  })

  function validarAuto() {
    const dia = Number(formAuto.dia)
    if (!(Number(formAuto.valor) > 0)) { setErro('Informe o valor da recarga.'); return }
    if (!Number.isInteger(dia) || dia < 1 || dia > 28) { setErro('O dia vai de 1 a 28.'); return }
    setErro(null)
    salvarAuto.mutate()
  }

  const saldo = cartao.saldo ?? 0

  return (
    <>
      <Card>
        <CardHeader
          title={<>{prePago ? 'Saldo e compras' : 'Compras no débito'} · {cartao.nome}</>}
          actions={
            <>
              <MonthNavigator mes={mes} ano={ano} onChange={onMudarMes} />
              <Button variant="light" icon="ri-pencil-line" onClick={onEditar}>Editar cartão</Button>
              {prePago && <Button variant="soft-success" icon="ri-arrow-up-circle-line" onClick={abrirRecarga}>Recarregar</Button>}
              <Button icon="ri-add-line" onClick={onLancarCompra}>Lançar compra</Button>
            </>
          }
        />

        <div className="grid gap-px border-b border-border/60 bg-border/60 sm:grid-cols-3">
          <div className="bg-surface p-4">
            <p className="text-2xs uppercase text-muted">{prePago ? 'Saldo do cartão' : 'Conta'}</p>
            {prePago ? (
              <>
                <p className={cn('text-xl font-semibold tabular-nums', saldo < 0 ? 'text-expense' : 'text-white')}>{formatMoney(saldo)}</p>
                {saldo < 0 && <p className="mt-1 text-2xs text-expense"><i className="ri-error-warning-line" aria-hidden /> Saldo negativo: registre a recarga ou dê baixa nela.</p>}
              </>
            ) : (
              <p className="text-xl font-semibold text-white">{cartao.accountName}</p>
            )}
          </div>
          <div className="bg-surface p-4">
            <p className="text-2xs uppercase text-muted">Gasto no mês</p>
            <p className="text-xl font-semibold tabular-nums text-expense">{formatMoney(gasto)}</p>
          </div>
          <div className="bg-surface p-4">
            <p className="text-2xs uppercase text-muted">{prePago ? 'Recarregado no mês' : 'Sai da conta'}</p>
            <p className="text-xl font-semibold tabular-nums text-white">{prePago ? formatMoney(recargas) : 'Na hora'}</p>
          </div>
        </div>

        {isLoading ? <LoadingState /> : itens.length === 0 ? (
          <CardBody><EmptyState icon="ri-shopping-bag-3-line" message="Nenhuma movimentação neste mês." /></CardBody>
        ) : (
          <>
          <div className="border-b border-border/60 p-4">
            <div className="w-64">
              <Input placeholder="Buscar por nome" value={ordenacao.busca} onChange={(e) => ordenacao.setBusca(e.target.value)} />
            </div>
          </div>
          <Table>
            <THead>
              <tr>
                <ThOrdenavel campo="nome" ativo={ordenacao.ordenarPor} desc={ordenacao.ordemDesc} onClick={ordenacao.alternarOrdenacao}>Descrição</ThOrdenavel>
                <ThOrdenavel campo="data" ativo={ordenacao.ordenarPor} desc={ordenacao.ordemDesc} onClick={ordenacao.alternarOrdenacao}>Data</ThOrdenavel>
                <Th>Situação</Th>
                <ThOrdenavel campo="valor" ativo={ordenacao.ordenarPor} desc={ordenacao.ordemDesc} onClick={ordenacao.alternarOrdenacao} className="text-right">Valor</ThOrdenavel>
              </tr>
            </THead>
            <tbody>
              {ordenacao.itens.map((t) => {
                const receita = t.type === TransactionType.Income
                return (
                  <Tr key={t.id}>
                    <Td>
                      <p className="font-medium text-white">{t.description}</p>
                      {t.categoriaNome && <p className="text-2xs text-muted">{t.categoriaNome}</p>}
                    </Td>
                    <Td className="whitespace-nowrap text-muted">{formatDate(t.date)}</Td>
                    <Td>{t.status === TransactionStatus.Pending ? <Badge variant="muted">Pendente</Badge> : <span className="text-muted">Confirmada</span>}</Td>
                    <Td className={cn('whitespace-nowrap text-right font-semibold tabular-nums', receita ? 'text-income' : 'text-expense')}>
                      {receita ? '+ ' : '− '}{formatMoney(t.amount)}
                    </Td>
                  </Tr>
                )
              })}
            </tbody>
          </Table>
          </>
        )}
      </Card>

      {prePago && (
        <Card className="mt-6">
          <CardHeader
            title="Recarga automática"
            actions={<Button variant="soft-primary" size="sm" icon="ri-add-line" onClick={() => abrirAuto(null)}>Nova recarga automática</Button>}
          />
          <CardBody className="space-y-3">
            <p className="text-13 text-muted">
              Para o depósito que a empresa faz todo mês. Ela nasce <strong className="text-white">pendente</strong> na data e aparece como previsto; quando o dinheiro cair, dê baixa em Transações e o saldo sobe.
              Também aparece em Recorrências.
            </p>
            {automaticas.length === 0 ? (
              <p className="py-4 text-center text-13 text-muted">Nenhuma recarga automática. Crie uma para não precisar lançar todo mês.</p>
            ) : (
              <ul className="divide-y divide-border/50 overflow-hidden rounded border border-border/60">
                {automaticas.map((r) => (
                  <li key={r.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                    <div className="min-w-0 flex-1">
                      <p className="flex flex-wrap items-center gap-2 text-white">
                        <span className="font-semibold tabular-nums">{formatMoney(r.valor)}</span>
                        <span className="text-13 text-muted">todo dia {r.dia}</span>
                        <Badge variant={r.ativa ? 'income' : 'muted'}>{r.ativa ? 'Ativa' : 'Pausada'}</Badge>
                      </p>
                      <p className="text-2xs text-muted">{r.ativa && r.proximaData ? `Próxima: ${formatDate(r.proximaData)}` : 'Não está gerando recargas.'}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="light" onClick={() => alternarAuto.mutate(r)} loading={alternarAuto.isPending && alternarAuto.variables?.id === r.id}>
                        {r.ativa ? 'Pausar' : 'Retomar'}
                      </Button>
                      <IconButton icon="ri-pencil-fill" label="Editar recarga automática" tone="info" onClick={() => abrirAuto(r)} />
                      <IconButton icon="ri-delete-bin-fill" label="Cancelar recarga automática" tone="danger" onClick={() => setCancelando(r)} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      )}

      <Modal
        open={recargaAberta}
        onClose={() => setRecargaAberta(false)}
        title={`Recarregar · ${cartao.nome}`}
        size="sm"
        footer={
          <>
            <Button variant="light" onClick={() => setRecargaAberta(false)}>Cancelar</Button>
            <Button icon="ri-arrow-up-circle-line" loading={recarregar.isPending} onClick={() => (Number(recarga.valor) > 0 ? (setErro(null), recarregar.mutate()) : setErro('Informe o valor da recarga.'))}>Recarregar</Button>
          </>
        }
      >
        <div className="grid gap-4">
          <Input label="Valor (R$)" type="number" min={0} step="0.01" value={recarga.valor} onChange={(e) => setRecarga((f) => ({ ...f, valor: e.target.value }))} autoFocus />
          <Input label="Data" type="date" value={recarga.date} onChange={(e) => setRecarga((f) => ({ ...f, date: e.target.value }))} />
          <Input label="Descrição" value={recarga.descricao} onChange={(e) => setRecarga((f) => ({ ...f, descricao: e.target.value }))} placeholder={`Recarga · ${cartao.nome}`} />
          <p className="text-13 text-muted">Entra como receita "Benefícios" e soma no saldo do cartão. Com data futura, fica pendente até a baixa. Se o dinheiro sair da sua conta, use uma Transferência.</p>
          {erro && <p className="flex items-center gap-1.5 rounded bg-expense/10 px-3 py-2 text-13 text-expense" role="alert"><i className="ri-error-warning-line" aria-hidden /> {erro}</p>}
        </div>
      </Modal>

      <Modal
        open={formAuto.aberto}
        onClose={() => setFormAuto((f) => ({ ...f, aberto: false }))}
        title={formAuto.editando ? 'Editar recarga automática' : 'Nova recarga automática'}
        size="sm"
        footer={
          <>
            <Button variant="light" onClick={() => setFormAuto((f) => ({ ...f, aberto: false }))}>Cancelar</Button>
            <Button icon="ri-save-line" loading={salvarAuto.isPending} onClick={validarAuto}>Salvar</Button>
          </>
        }
      >
        <div className="grid gap-4">
          <Input label="Valor (R$)" type="number" min={0} step="0.01" value={formAuto.valor} onChange={(e) => setFormAuto((f) => ({ ...f, valor: e.target.value }))} autoFocus />
          <Input label="Dia do mês (1 a 28)" type="number" min={1} max={28} value={formAuto.dia} onChange={(e) => setFormAuto((f) => ({ ...f, dia: e.target.value }))} />
          {formAuto.editando && <p className="text-13 text-muted">A mudança vale para as próximas recargas. O que já foi baixado não muda.</p>}
          {erro && <p className="flex items-center gap-1.5 rounded bg-expense/10 px-3 py-2 text-13 text-expense" role="alert"><i className="ri-error-warning-line" aria-hidden /> {erro}</p>}
        </div>
      </Modal>

      <Modal
        open={!!cancelando}
        onClose={() => setCancelando(null)}
        title="Cancelar recarga automática"
        size="sm"
        footer={
          <>
            <Button variant="light" onClick={() => setCancelando(null)}>Voltar</Button>
            <Button variant="danger" loading={cancelarAuto.isPending} onClick={() => cancelando && cancelarAuto.mutate(cancelando)}>Cancelar recarga</Button>
          </>
        }
      >
        <p className="text-13 text-muted">
          A recarga de <strong className="text-white">{formatMoney(cancelando?.valor ?? 0)}</strong> todo dia {cancelando?.dia} deixa de acontecer e a próxima pendente é removida.
          O que já foi baixado continua no histórico. Para só parar por um tempo, use Pausar.
        </p>
      </Modal>
    </>
  )
}
